/**
 * LineSegmentHandles Component
 *
 * Renders visual indicator handles at the midpoint of each line segment.
 * These are ALWAYS VISIBLE and serve as visual feedback that the line is draggable.
 *
 * Features:
 * - Non-draggable markers (visual indicators only)
 * - Positioned at midpoint between consecutive points (accounting for line type)
 * - Always visible (not hover-dependent)
 * - Subtle styling to not distract from main content
 *
 * @module components/map/GeometryRenderer/LineRenderer/LineSegmentHandles
 */

import { memo, useMemo } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { LineType } from '../../../../types/geometry';
import { getSegmentWithContext } from './utils/lineGeoJsonHelpers';
import type { TaskGeometry, Coordinates } from '../../../../types';
import classes from './LineSegmentHandles.module.css';

/**
 * Props for LineSegmentHandles component
 */
export interface LineSegmentHandlesProps {
  /** The line geometry containing points */
  geometry: TaskGeometry;
  /** Whether drag mode is enabled (controls visibility) */
  isDraggable: boolean;
  /** Whether this geometry is currently being dragged (hides handles during drag) */
  isDragging?: boolean;
}

/**
 * Calculate midpoint of a curved segment using Catmull-Rom spline at t=0.5
 *
 * @param p0 - Control point before p1
 * @param p1 - Start point of segment
 * @param p2 - End point of segment
 * @param p3 - Control point after p2
 * @returns Midpoint on the curved segment
 */
function calculateCurvedMidpoint(
  p0: Coordinates,
  p1: Coordinates,
  p2: Coordinates,
  p3: Coordinates
): Coordinates {
  const t = 0.5; // Midpoint parameter
  const t2 = t * t;
  const t3 = t2 * t;

  // Catmull-Rom formula at t=0.5
  const CATMULL_ROM_FACTOR = 0.5;

  const lat =
    CATMULL_ROM_FACTOR *
    (2 * p1.lat +
      (-p0.lat + p2.lat) * t +
      (2 * p0.lat - 5 * p1.lat + 4 * p2.lat - p3.lat) * t2 +
      (-p0.lat + 3 * p1.lat - 3 * p2.lat + p3.lat) * t3);

  const lng =
    CATMULL_ROM_FACTOR *
    (2 * p1.lng +
      (-p0.lng + p2.lng) * t +
      (2 * p0.lng - 5 * p1.lng + 4 * p2.lng - p3.lng) * t2 +
      (-p0.lng + 3 * p1.lng - 3 * p2.lng + p3.lng) * t3);

  return { lat, lng };
}

/**
 * LineSegmentHandles Component
 *
 * Renders small circles at segment midpoints as visual drag indicators
 */
export const LineSegmentHandles = memo(function LineSegmentHandles({
  geometry,
  isDraggable,
  isDragging = false,
}: LineSegmentHandlesProps) {
  // Calculate midpoints for all segments (memoized for performance)
  const segmentMidpoints = useMemo(() => {
    const linePoints = geometry.linePoints || [];
    const parentIndices = geometry.parentIndices || [];
    const lineType = geometry.lineType || LineType.Straight;
    const midpoints: Coordinates[] = [];

    // CASE 1: No parentIndices → Backward compatible (sequential line)
    if (parentIndices.length === 0) {
      // Sequential iteration for old lines without branching support
      for (let i = 0; i < linePoints.length - 1; i++) {
        const p1 = linePoints[i];
        const p2 = linePoints[i + 1];

        if (p1 && p2) {
          if (lineType === LineType.Curved && linePoints.length >= 3) {
            // Calculate curved midpoint using Catmull-Rom interpolation
            let p0, p3;

            if (i === 0) {
              // First segment: duplicate first point
              p0 = linePoints[0];
              p3 = linePoints[2] || linePoints[1];
            } else if (i === linePoints.length - 2) {
              // Last segment: duplicate last point
              p0 = linePoints[i - 1];
              p3 = linePoints[i + 1];
            } else {
              // Middle segments
              p0 = linePoints[i - 1];
              p3 = linePoints[i + 2];
            }

            if (p0 && p3) {
              midpoints.push(calculateCurvedMidpoint(p0, p1, p2, p3));
            }
          } else {
            // Straight line: simple midpoint calculation
            midpoints.push({
              lat: (p1.lat + p2.lat) / 2,
              lng: (p1.lng + p2.lng) / 2,
            });
          }
        }
      }
    } else {
      // CASE 2: Has parentIndices → Use parent-child pairing (supports branching)
      linePoints.forEach((_point, index) => {
        const parentIdx = parentIndices[index];

        // Skip root points (no parent)
        if (parentIdx === null || parentIdx === undefined) return;

        const p1 = linePoints[parentIdx]; // Parent (start of segment)
        const p2 = linePoints[index]; // Child (end of segment)

        if (!p1 || !p2) return;

        if (lineType === LineType.Curved && linePoints.length >= 3) {
          // Get proper Catmull-Rom context for this segment in branching mode
          const segmentContext = getSegmentWithContext(
            parentIdx,
            index,
            linePoints,
            parentIndices
          );

          // segmentContext returns [p0, p1, p2, p3] - we need p0 and p3 for context
          if (segmentContext.length >= 4) {
            const [p0, _p1, _p2, p3] = segmentContext;
            if (p0 && p3) {
              midpoints.push(calculateCurvedMidpoint(p0, p1, p2, p3));
            }
          }
        } else {
          // Straight line: simple midpoint calculation
          midpoints.push({
            lat: (p1.lat + p2.lat) / 2,
            lng: (p1.lng + p2.lng) / 2,
          });
        }
      });
    }

    return midpoints;
  }, [geometry.linePoints, geometry.lineType, geometry.parentIndices]);

  // Don't render if: drag disabled, currently dragging, or insufficient points
  if (
    !isDraggable ||
    isDragging ||
    !geometry.linePoints ||
    geometry.linePoints.length < 2
  ) {
    return null;
  }

  return (
    <>
      {segmentMidpoints.map((midpoint: Coordinates, index: number) => (
        <Marker
          key={`segment-handle-${geometry.id}-${index}`}
          longitude={midpoint.lng}
          latitude={midpoint.lat}
          anchor="center"
        >
          <div className={classes.segmentIndicator}>
            {/* Visual indicator - small gray circle */}
            <div className={classes.indicatorInner} />
          </div>
        </Marker>
      ))}
    </>
  );
});
